import { can } from '../core/access';
import { clockIconSvg, formatAge, formatClock } from '../core/format';
import { refreshIconSvg } from '../stream/stream-controls-shared';
import { t } from '../ui/lang';
import { esc } from '../ui/modal';
const REFRESH_MS = 20000;
const STATUS_ORDER = { offline: 0, farm_offline: 1, online: 2 };
function kindLabel(kind) {
    switch (kind) {
        case 'host': return t('dashboards.kind_host');
        case 'aggregator': return t('dashboards.kind_aggregator');
        case 'duo': return 'Duo';
        case 'geelark': return 'GeeLark';
        default: return kind;
    }
}
// Stroked 24×24 glyphs in the house style; colour comes from the .roster-status--* class.
const STATUS_ICONS = {
    online: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="2.5" width="12" height="19" rx="2.5"/><path d="m9 12 2 2 4-4"/></svg>',
    offline: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="2.5" width="12" height="19" rx="2.5"/><path d="m9.5 9.5 5 5"/><path d="m14.5 9.5-5 5"/></svg>',
    farm_offline: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="6" rx="1.5"/><rect x="3" y="14" width="18" height="6" rx="1.5"/><line x1="2" y1="2" x2="22" y2="22"/></svg>',
};
function statusCell(d) {
    const label = t(`dashboards.status_${d.status}`);
    return `<span class="roster-status roster-status--${d.status}">${STATUS_ICONS[d.status]}${esc(label)}</span>`;
}
function seenCell(d, now) {
    const ts = d.offlineSince ?? d.lastOnlineAt;
    const text = d.offlineSince
        ? t('dashboards.offline_for', { age: formatAge(d.offlineSince, now) })
        : t('dashboards.online_now');
    return `<span class="device-updated" title="${esc(new Date(ts).toLocaleString())}">${clockIconSvg}<span class="device-updated__time">${esc(text)}${d.offlineSince ? ` · ${esc(formatClock(d.offlineSince))}` : ''}</span></span>`;
}
export function renderDevicesTab(panel) {
    const canForget = can('dashboards.devices.forget');
    // Only a superadmin reads more than one team (same gate as the operations registry).
    const showTeam = can('ops.team');
    panel.innerHTML = `
        <div class="clicker-ops-toolbar">
            <label class="ops-filter">${t('common.status')}
                <select class="settings-input dev-status">
                    <option value="">${t('common.all')}</option>
                    <option value="online">${t('dashboards.status_online')}</option>
                    <option value="offline">${t('dashboards.status_offline')}</option>
                    <option value="farm_offline">${t('dashboards.status_farm_offline')}</option>
                </select>
            </label>
            ${showTeam ? `<label class="ops-filter">${t('common.team')}
                <select class="settings-input dev-team"><option value="">${t('common.all')}</option></select>
            </label>` : ''}
            <label class="ops-filter">${t('common.farm')}
                <select class="settings-input dev-farm"><option value="">${t('common.all')}</option></select>
            </label>
            <label class="ops-filter">${t('common.search')}
                <input type="search" class="settings-input dev-search" placeholder="${t('dashboards.search_placeholder')}" />
            </label>
            <span class="clicker-ops-spacer"></span>
            <span class="roster-counters"></span>
            <button type="button" class="tbl-btn farm-refresh-btn dev-refresh" title="${t('common.refresh')}" aria-label="${t('common.refresh')}">${refreshIconSvg}</button>
        </div>
        <div class="roster-body"><p class="settings-hint">${t('common.loading')}</p></div>
    `;
    const statusEl = panel.querySelector('.dev-status');
    const farmEl = panel.querySelector('.dev-farm');
    const teamEl = panel.querySelector('.dev-team');
    const searchEl = panel.querySelector('.dev-search');
    const countersEl = panel.querySelector('.roster-counters');
    const bodyEl = panel.querySelector('.roster-body');
    let devices = [];
    let loaded = false;
    const fillSelect = (el, items) => {
        const selected = el.value;
        const opts = [...items].sort((a, b) => a[1].localeCompare(b[1]))
            .map(([id, name]) => `<option value="${esc(id)}">${esc(name)}</option>`).join('');
        el.innerHTML = `<option value="">${t('common.all')}</option>${opts}`;
        if (items.has(selected))
            el.value = selected;
    };
    const teamKey = (d) => String(d.teamId ?? '');
    // The farm list follows the chosen team, so it never offers a farm the table would hide.
    const fillFarms = () => {
        const team = teamEl?.value ?? '';
        const farms = new Map();
        for (const d of devices)
            if (!team || teamKey(d) === team)
                farms.set(String(d.farmId), d.farmName);
        fillSelect(farmEl, farms);
    };
    const fillFilters = () => {
        if (teamEl) {
            const teams = new Map();
            for (const d of devices)
                if (d.teamId !== null)
                    teams.set(teamKey(d), d.teamName);
            fillSelect(teamEl, teams);
        }
        fillFarms();
    };
    const render = () => {
        const now = Date.now();
        const count = (s) => devices.filter((d) => d.status === s).length;
        countersEl.innerHTML = t('dashboards.counters', {
            total: devices.length, online: count('online'), offline: count('offline'), farmOffline: count('farm_offline'),
        });
        const status = statusEl.value;
        const team = teamEl?.value ?? '';
        const farm = farmEl.value;
        const q = searchEl.value.trim().toLowerCase();
        const list = devices
            .filter((d) => !status || d.status === status)
            .filter((d) => !team || teamKey(d) === team)
            .filter((d) => !farm || String(d.farmId) === farm)
            .filter((d) => !q || [d.name, d.udid, d.model].some((v) => v.toLowerCase().includes(q)))
            .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
            || (b.offlineSince ?? 0) - (a.offlineSince ?? 0)
            || a.farmName.localeCompare(b.farmName)
            || (a.name || a.udid).localeCompare(b.name || b.udid));
        if (list.length === 0) {
            bodyEl.innerHTML = `<p class="settings-hint">${t(devices.length ? 'dashboards.no_match' : 'dashboards.no_devices')}</p>`;
            return;
        }
        const rows = list.map((d) => `
            <tr class="roster-row roster-row--${d.status}">
                <td>${esc(d.name || d.model || d.udid)}</td>
                <td><code>${esc(d.udid)}</code></td>
                <td><a href="#!/farms/${d.farmId}">${esc(d.farmName)}</a></td>
                <td>${esc(kindLabel(d.farmKind))}</td>
                <td>${esc(d.model || '—')}</td>
                <td>${d.battery === null ? '—' : `${d.battery}%`}</td>
                <td>${statusCell(d)}</td>
                <td>${seenCell(d, now)}</td>
                ${canForget ? `<td>${d.status === 'online' ? '' : `<button type="button" class="tbl-btn delete-btn dev-forget" data-farm="${d.farmId}" data-udid="${esc(d.udid)}">${t('dashboards.forget')}</button>`}</td>` : ''}
            </tr>`).join('');
        bodyEl.innerHTML = `
            <div class="clicker-ops-table-wrap roster-table-wrap">
                <table class="access-matrix roster-table">
                    <thead><tr>
                        <th>${t('common.name')}</th><th>UDID</th><th>${t('common.farm')}</th>
                        <th>${t('dashboards.col_kind')}</th><th>${t('dashboards.col_model')}</th>
                        <th>${t('dashboards.col_battery')}</th><th>${t('common.status')}</th>
                        <th>${t('dashboards.col_seen')}</th>${canForget ? '<th></th>' : ''}
                    </tr></thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
        bodyEl.querySelectorAll('.dev-forget').forEach((btn) => {
            btn.addEventListener('click', () => { void forget(btn); });
        });
    };
    const forget = async (btn) => {
        const farmId = btn.dataset['farm'];
        const udid = btn.dataset['udid'];
        if (!confirm(t('dashboards.confirm_forget', { udid })))
            return;
        btn.disabled = true;
        try {
            const r = await fetch(`/api/devices/roster/${farmId}/${encodeURIComponent(udid)}`, { method: 'DELETE' });
            if (!r.ok)
                throw new Error(String(r.status));
            devices = devices.filter((d) => !(String(d.farmId) === farmId && d.udid === udid));
            fillFilters();
            render();
        }
        catch {
            btn.disabled = false;
            alert(t('common.failed_to_delete'));
        }
    };
    const load = async () => {
        try {
            const r = await fetch('/api/devices/roster');
            if (!r.ok)
                throw new Error(String(r.status));
            devices = (await r.json()).devices;
            loaded = true;
            fillFilters();
            render();
        }
        catch {
            // Keep the last good table on a failed refresh; only an empty tab shows the error.
            if (!loaded)
                bodyEl.innerHTML = `<p class="settings-hint settings-hint--err">${t('common.failed_to_load')}</p>`;
        }
    };
    statusEl.addEventListener('change', render);
    farmEl.addEventListener('change', render);
    teamEl?.addEventListener('change', () => { fillFarms(); render(); });
    searchEl.addEventListener('input', render);
    panel.querySelector('.dev-refresh').addEventListener('click', () => { void load(); });
    // The router swaps #content on navigation, so a detached panel is the stop signal.
    const timer = window.setInterval(() => {
        if (!panel.isConnected) {
            window.clearInterval(timer);
            return;
        }
        void load();
    }, REFRESH_MS);
    void load();
}
